"use client";

// WO-GF-001 · DESIGN-SYSTEM card 86bb58y52 — perks/category icons served from the design system.
//
// DOCUMENT-SCOPED HYDRATOR, the same family as StopsMediaLayer / ScheduleDataLayer / HubChromeLayer:
// it rewrites captured mirror markup at runtime rather than editing the capture. That is not a style
// preference, it is the standing rail — `apps/root` serves inlined captured CSSOM per page, so
// touching the captured HTML moves every mirror pixel baseline and (for `var(--sg-*)`) resolves to
// nothing at all. One layer, mounted once in the shell, reaches all 1152 legacy icon references.
//
// WHAT IT CHANGES: the `src` of `<img>` elements pointing at the legacy Strapi `common/` icon store,
// and nothing else. The element, its classes, its width/height attributes and its position in the
// document are untouched, so the layout box is identical before and after.
//
// THE ONE ADDED STYLE, and why it is required rather than cosmetic: the captured slot is 111×58
// (landscape), while the design-system icons carry their own varied intrinsic ratios (rentals is
// 258×321 portrait, transport 394×323 landscape). Dropped into a fixed 111×58 box with the mirror's
// default `object-fit: fill`, EVERY icon would stretch. `object-fit: contain` letterboxes the art
// inside the box the mirror already reserved — the box does not move, so no layout reflows.
//
// HONESTY RAIL: an icon we hold no mapping for is LEFT ALONE, counted, and published on
// `data-ds-icons-unmapped`. It is never swapped for a near-enough icon. The counts are published on
// <html> so the gate reads what actually happened rather than trusting that this ran.

import { useEffect } from "react";
import { isLegacyCommonIcon, resolveLegacyIcon } from "@/lib/design-system-icons";

export default function DesignSystemIconLayer() {
  useEffect(() => {
    const root = document.documentElement;

    const swap = () => {
      let swapped = 0;
      let unmapped = 0;

      for (const img of Array.from(document.images)) {
        // `img.src` is the RESOLVED absolute URL; the attribute may be relative. The legacy store is
        // absolute either way, and reading the resolved form means a re-run cannot re-match an image
        // this layer already pointed at `/design-system/...`.
        const src = img.src;
        if (!isLegacyCommonIcon(src)) continue;

        const hit = resolveLegacyIcon(src);
        if (!hit) {
          // No honest mapping (hero art, rental tier badges). Mark it so the gap is visible in the
          // DOM instead of being indistinguishable from "this layer never ran".
          img.dataset.dsIconUnmapped = src.split("/").pop() ?? "";
          unmapped += 1;
          continue;
        }

        img.dataset.dsIcon = hit.name;
        img.dataset.dsIconLegacySrc = src;
        img.style.objectFit = "contain";
        // `srcset` wins over `src` on the captured Next.js <img>; leaving it set would silently keep
        // the legacy artwork on screen while `src` reported the new one — a green check over an
        // unchanged pixel, which is exactly the failure class this lane keeps re-learning.
        img.removeAttribute("srcset");
        // The captured markup carries `loading="lazy"`, and these perk icons sit ~98% down the
        // document. Left lazy, a swapped icon does not fetch until it is scrolled to — so the new
        // artwork is neither on screen nor measurable, and a "did it load" read is answering a
        // question about the viewport rather than about the file. Dropping it is safe: these are
        // sub-10KB local SVGs, not the hero photography lazy loading exists for.
        img.removeAttribute("loading");
        img.src = hit.url;
        swapped += 1;
      }

      root.dataset.dsIconsSwapped = String(swapped);
      root.dataset.dsIconsUnmapped = String(unmapped);
      root.dataset.dsIconsReady = "true";
    };

    swap();

    // The componentized browse/filter branches re-render cards after their fetches land (the same
    // race S35 measured on `[data-mountain-details]`). Observing subtree src changes keeps late
    // cards covered without a sleep, which would pass here and flake on a slower machine.
    const obs = new MutationObserver(swap);
    obs.observe(document.body, { childList: true, subtree: true, attributeFilter: ["src"] });
    return () => obs.disconnect();
  }, []);

  return null;
}
